import { useCallback } from 'react';
import { clientsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Client } from '../types';

export function useClients(
  options?: ResourceQueryOptions
): ResourceState<Client> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      clientsService.getAll(opts) as unknown as Promise<{ data: Client[]; meta?: any }>,
    []
  );
  return useResourceCollection<Client>(fetchFn, options);
}

export function useClient(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Client> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (clientId: number, opts?: ResourceQueryOptions) =>
      clientsService.getById(clientId, opts) as unknown as Promise<Client>,
    []
  );
  return useResourceById<Client>(fetchFn, numericId, options);
}

export function useCreateClient(
  onSuccess?: (data: Client) => void
): MutationState<Client> {
  const createFn = useCallback(
    (data: any) => clientsService.create(data) as unknown as Promise<Client>,
    []
  );
  return useCreateResource<Client>(createFn, onSuccess);
}

export function useUpdateClient(
  onSuccess?: (data: Client) => void
): MutationState<Client> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return clientsService.update(numericId, data) as unknown as Promise<Client>;
    },
    []
  );
  return useUpdateResource<Client>(updateFn, onSuccess);
}

/**
 * Delete client using documentId
 * @param data - Full client object containing documentId
 */
export function useDeleteClient(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return clientsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
